import React, { useState, useEffect, useCallback } from 'react';
import { priceBookAPI } from '../lib/api';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Search, Loader2, Layers, Plus, Check, Wrench, Package } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_BADGE = {
  service: { label: 'Service', cls: 'bg-blue-100 text-blue-700' },
  material: { label: 'Material', cls: 'bg-amber-100 text-amber-700' },
  labor: { label: 'Labor', cls: 'bg-purple-100 text-purple-700' },
};

export function ServiceTemplatePicker({ open, onOpenChange, estimateId, optionId, onApply }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [applying, setApplying] = useState(null);

  const fetchTemplates = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await priceBookAPI.listTemplates({ search: search || undefined });
      setTemplates(res.data.templates || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [open, search]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleApply = async (template) => {
    setApplying(template.id);
    try {
      const res = await priceBookAPI.addToEstimate(template.id, estimateId, { option_id: optionId, quantity: 1 });
      const count = res.data.items_added;
      toast.success(`Applied "${template.name}" — ${count} item${count > 1 ? 's' : ''} added`);
      if (onApply) onApply(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply template');
    } finally { setApplying(null); }
  };

  const toggle = (id) => setExpanded(expanded === id ? null : id);

  return (
    <AppDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Service Templates"
      description="Select a template to auto-fill estimate with predefined line items."
      footer={<Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>}
    >
      <div className="space-y-3" data-testid="template-picker">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search templates..."
            className="pl-9 h-9"
            data-testid="template-search"
          />
        </div>

        <div className="space-y-2 max-h-[460px] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="no-templates">
              <Layers className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm font-medium">No templates yet</p>
              <p className="text-xs mt-1">Create service templates in the Price Book to quickly fill estimates.</p>
            </div>
          ) : templates.map(t => (
            <Card
              key={t.id}
              className={`transition-all cursor-pointer hover:shadow-sm ${expanded === t.id ? 'ring-1 ring-primary/30' : ''}`}
              data-testid={`template-card-${t.id}`}
            >
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center mt-0.5">
                    <Wrench className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0" onClick={() => toggle(t.id)}>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{t.name}</span>
                      <Badge variant="outline" className="text-[10px]">
                        <Package className="w-2.5 h-2.5 mr-0.5" />{t.component_count} item{t.component_count !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    {t.description && <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>}
                    <p className="text-sm font-bold mt-1" data-testid={`template-total-${t.id}`}>${t.template_total.toFixed(2)}</p>
                  </div>
                  <Button
                    size="sm"
                    className="h-8 shrink-0 gap-1"
                    onClick={(e) => { e.stopPropagation(); handleApply(t); }}
                    disabled={applying === t.id}
                    data-testid={`apply-template-${t.id}`}
                  >
                    {applying === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    Apply
                  </Button>
                </div>

                {/* Component preview */}
                {expanded === t.id && t.components.length > 0 && (
                  <div className="mt-3 pt-3 border-t space-y-1" data-testid={`template-preview-${t.id}`}>
                    {t.components.map((c, idx) => {
                      const badge = TYPE_BADGE[c.item_type] || TYPE_BADGE.service;
                      return (
                        <div key={c.id || idx} className="flex items-center justify-between text-xs py-1" data-testid={`template-comp-${idx}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <Badge className={`${badge.cls} text-[9px] shrink-0`}>{badge.label}</Badge>
                            <span className="truncate">{c.name}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 text-muted-foreground">
                            <span>{c.quantity}x</span>
                            <span className="font-medium text-foreground">${c.unit_price.toFixed(2)}</span>
                            <span>=</span>
                            <span className="font-semibold text-foreground">${c.line_total.toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AppDrawer>
  );
}
